import { intlLocale } from './i18n';
import type { DashOrder } from './cockpit';
import type { ApprovalItem } from './dashboard';
import {
  inRange,
  moneyBySupplier,
  orderedByCategory,
  potentialOf,
  rangeOf,
  realizedOf,
  trendPct,
  type CategorySpend,
  type Range,
  type RfqStat,
} from './panel';

/**
 * Raportul pentru conducere (patron, director, CFO), 2026-10-27: cei care plătesc
 * Procumi nu lucrează în el zilnic, deci vor un singur ecran (și un mail pe lună)
 * cu ce contează: cât s-a comandat, cât s-a economisit, unde nu s-a ales cea mai
 * mică ofertă, ce așteaptă aprobarea lor și cât de mult depinde firma de un furnizor.
 * Economiile urmează regula de pe panou: față de următoarea ofertă completă.
 */

export interface PricierChoice {
  rfqId: string;
  title: string;
  site: string;
  /** prețul furnizorului ales */
  chosen: number;
  /** cea mai mică ofertă completă */
  cheapest: number;
  diff: number;
}

export interface OwnerReport {
  ordered: { value: number; count: number; prev: number; trend: number | null };
  savings: {
    /** obținut: cererile hotărâte în perioadă */
    realized: number;
    realizedCount: number;
    /** pierdut: cererile hotărâte peste cel mai bun preț posibil */
    missed: number;
    /** cereri hotărâte în perioadă */
    decided: number;
  };
  /** cererile hotărâte peste cea mai mică ofertă completă: poate cu motiv (termen, calitate), de verificat */
  pricier: PricierChoice[];
  approvals: { count: number; value: number };
  categories: CategorySpend[];
  suppliers: { name: string; value: number; share: number }[];
  /** cât ia cel mai mare furnizor din valoarea comandată (0..1) */
  topShare: number;
  competition: {
    /** cereri trimise în perioadă */
    rfqs: number;
    /** oferte primite în medie pe cerere trimisă; null fără cereri */
    offersPerRfq: number | null;
    /** cereri trimise cu cel mult o ofertă primită: fără concurență */
    single: number;
  };
}

/** Prețul furnizorului ales: oferta lui completă sau valoarea scrisă la alegere. */
const chosenPrice = (r: RfqStat): number | null =>
  r.sends.find((s) => s.supplierId === r.chosenSupplierId)?.total ?? r.chosenTotal;

export function ownerReport(
  d: { stats: RfqStat[]; orders: DashOrder[]; toApprove?: ApprovalItem[] },
  range: Range,
): OwnerReport {
  const inP = d.orders.filter((o) => inRange(o.issuedAt, range.from, range.to) && o.valueLei > 0);
  const inPrev = d.orders.filter((o) => inRange(o.issuedAt, range.prevFrom, range.prevTo) && o.valueLei > 0);
  const value = inP.reduce((n, o) => n + o.valueLei, 0);
  const prev = inPrev.reduce((n, o) => n + o.valueLei, 0);

  const decided = d.stats.filter((r) => r.chosenSupplierId && inRange(r.chosenAt, range.from, range.to));
  let realized = 0;
  let realizedCount = 0;
  let missed = 0;
  const pricier: PricierChoice[] = [];
  for (const r of decided) {
    const got = realizedOf(r);
    realized += got;
    if (got > 0) realizedCount += 1;
    missed += Math.max(0, potentialOf(r) - got);
    const own = chosenPrice(r);
    const totals = r.sends.map((s) => s.total).filter((t): t is number => t != null);
    if (own != null && totals.length > 1) {
      const cheapest = Math.min(...totals);
      if (own > cheapest + 0.5) {
        pricier.push({
          rfqId: r.id,
          title: r.title,
          site: r.site.name,
          chosen: own,
          cheapest,
          diff: own - cheapest,
        });
      }
    }
  }
  pricier.sort((a, b) => b.diff - a.diff);

  const bySupplier = moneyBySupplier(inP);
  const sent = d.stats.filter((r) =>
    r.sends.some((s) => s.sentAt && inRange(s.sentAt, range.from, range.to)),
  );
  const received = sent.map((r) => r.sends.filter((s) => s.receivedAt).length);

  return {
    ordered: { value, count: inP.length, prev, trend: trendPct(value, prev) },
    savings: { realized, realizedCount, missed, decided: decided.length },
    pricier: pricier.slice(0, 5),
    approvals: {
      count: d.toApprove?.length ?? 0,
      value: (d.toApprove ?? []).reduce((n, a) => n + a.valueLei, 0),
    },
    categories: orderedByCategory(inP, range.from, range.to),
    suppliers: bySupplier.slice(0, 5).map((s) => ({
      name: s.supplier.name,
      value: s.total,
      share: value ? s.total / value : 0,
    })),
    topShare: value && bySupplier[0] ? bySupplier[0].total / value : 0,
    competition: {
      rfqs: sent.length,
      offersPerRfq: sent.length ? received.reduce((n, x) => n + x, 0) / sent.length : null,
      single: received.filter((x) => x <= 1).length,
    },
  };
}

/**
 * Raportul lunar pleacă în primele 3 zile ale lunii, pentru luna trecută. Întoarce
 * luna ('AAAA-LL'), numele ei („octombrie 2026”) și perioada, sau null în rest.
 * Ziua e cea din România.
 */
export function reportMonthDue(
  now: Date,
  timeZone = 'Europe/Bucharest',
): { month: string; label: string; range: Range } | null {
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(now)
    .split('-')
    .map(Number) as [number, number, number];
  if (d > 3) return null;
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  const month = `${py}-${String(pm).padStart(2, '0')}`;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const range = rangeOf('custom', `${month}-01`, {
    from: `${month}-01`,
    to: `${month}-${String(last).padStart(2, '0')}`,
  });
  const label = new Intl.DateTimeFormat(intlLocale(), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(py, pm - 1, 15)));
  return { month, label, range };
}
